/**
 * useConfirmDialog — themed náhrada za window.confirm().
 *
 *   const { ask, dialog } = useConfirmDialog();
 *   // v handleru:
 *   if (!(await ask({ title: t('…'), description: t('…'), danger: true }))) return;
 *   // v JSX:
 *   <>{dialog}</>
 */
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';

export interface ConfirmAskOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

export function useConfirmDialog() {
  const { t } = useTranslation();
  const resolveRef = useRef<((v: boolean) => void) | null>(null);
  const [opts, setOpts] = useState<ConfirmAskOptions | null>(null);

  const close = useCallback((value: boolean) => {
    const r = resolveRef.current;
    resolveRef.current = null;
    setOpts(null);
    r?.(value);
  }, []);

  const ask = useCallback((options: ConfirmAskOptions) => {
    return new Promise<boolean>((resolve) => {
      // Pokud už visí dialog, zavři ho jako cancel
      if (resolveRef.current) resolveRef.current(false);
      resolveRef.current = resolve;
      setOpts(options);
    });
  }, []);

  const dialog = (
    <ConfirmDialog
      open={!!opts}
      title={opts?.title ?? ''}
      description={opts?.description}
      confirmLabel={opts?.confirmLabel}
      cancelLabel={opts?.cancelLabel ?? t('ui.cancel')}
      danger={opts?.danger}
      onConfirm={() => close(true)}
      onCancel={() => close(false)}
    />
  );

  return { ask, dialog, open: !!opts };
}
